import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ShieldCheck, LogOut, Building2, Users, Activity, Bell, CalendarClock,
  TrendingUp, ArrowRight, Loader2, AlertCircle, Eye, Globe, Sparkles, AlertTriangle, Zap, UserCog, BarChart3,
} from "lucide-react";

const TONE = {
  leaf: "hsl(var(--haruki-leaf))",
  teal: "hsl(var(--haruki-teal))",
  rust: "hsl(var(--haruki-rust))",
};

export default function PlatformDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/platform/stats").then((r) => setStats(r.data)),
      api.get("/platform/agencies").then((r) => setAgencies(r.data)),
    ])
      .catch((e) => setError(e?.response?.data?.detail || "Could not load platform data."))
      .finally(() => setLoading(false));
  }, []);

  const openDrill = async (agencyId) => {
    try {
      const r = await api.get(`/platform/agency/${agencyId}`);
      setDrill(r.data);
    } catch (e) {
      setError("Could not load agency drill-down.");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))" }}>
              <Globe className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">Haruki Health</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
                Platform Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-muted-foreground sm:inline">{user?.email}</span>
            <Button data-testid="platform-logout-button" variant="ghost" size="sm"
                    onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="mr-2 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
        <div className="mb-1 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--haruki-teal))" }}>
          Internal · Cross-customer view
        </div>
        <h1 className="font-heading text-3xl font-bold lg:text-4xl">Platform Overview</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Every agency on the platform with rollup stats. Aggregate participation data only — individual scenario
          content, individual scores, and SOS conversation content are never exposed to platform admins either.
        </p>

        {loading && (
          <div className="mt-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading platform data…
          </div>
        )}

        {error && (
          <div data-testid="platform-error" className="mt-10 rounded-md border p-6"
               style={{ borderColor: "hsl(var(--haruki-fire))", background: "hsl(var(--haruki-fire) / 0.06)" }}>
            <AlertCircle className="mb-2 h-5 w-5" style={{ color: "hsl(var(--haruki-fire))" }} />
            {error}
          </div>
        )}

        {stats && (
          <>
            {/* Top-line stats */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat icon={Building2} label="Agencies" value={stats.total_agencies}
                    subline={`${stats.trial_agencies} on trial`} tone="teal" test="platform-stat-agencies" />
              <Stat icon={Users} label="Service members" value={stats.total_members}
                    subline={`${stats.total_admins} admins`} tone="leaf" test="platform-stat-members" />
              <Stat icon={Activity} label="Active (30d)" value={stats.active_30d}
                    subline={`${stats.completions_30d} completions`} tone="leaf" test="platform-stat-active" />
              <Stat icon={Sparkles} label="Leads" value={stats.leads_total}
                    subline={`${stats.leads_30d} in last 30d`} tone="teal" test="platform-stat-leads" />
            </div>

            {/* Quick links */}
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/platform/intakes"
                className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
                data-testid="platform-intakes-link"
                style={{ color: "hsl(var(--haruki-leaf))" }}
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5" /> View trial intakes &amp; UTM data
              </Link>
              <Link
                to="/platform/users"
                className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
                data-testid="platform-users-link"
                style={{ color: "hsl(var(--haruki-teal))" }}
              >
                <Users className="mr-1.5 h-3.5 w-3.5" /> Users &amp; account status
              </Link>
              <Link
                to="/platform/hmde"
                className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
                data-testid="platform-hmde-link"
                style={{ color: "hsl(var(--haruki-teal))" }}
              >
                <Activity className="mr-1.5 h-3.5 w-3.5" /> HMDE health
              </Link>
              <Link
                to="/platform/buffer"
                className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
                data-testid="platform-buffer-link"
                style={{ color: "hsl(var(--haruki-teal))" }}
              >
                <Zap className="mr-1.5 h-3.5 w-3.5" /> Scenario buffer
              </Link>
              <Link
                to="/platform/demos"
                className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
                data-testid="platform-demos-link"
                style={{ color: "hsl(var(--haruki-leaf))" }}
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Prospect demos
              </Link>
              <Link
                to="/platform/personas"
                className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
                data-testid="platform-personas-link"
                style={{ color: "hsl(var(--haruki-leaf))" }}
              >
                <UserCog className="mr-1.5 h-3.5 w-3.5" /> Switch persona
              </Link>
              <Link
                to="/platform/speaking"
                className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
                data-testid="platform-speaking-link"
                style={{ color: "hsl(var(--haruki-leaf))" }}
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Speaking
              </Link>
              <Link
                to="/platform/analytics"
                className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
                data-testid="platform-analytics-link"
                style={{ color: "hsl(var(--haruki-teal))" }}
              >
                <BarChart3 className="mr-1.5 h-3.5 w-3.5" /> Site analytics
              </Link>
            </div>

            {/* Vertical breakdown */}
            <Card className="mt-6">
              <CardHeader><CardTitle className="font-heading text-base">Members by vertical</CardTitle></CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-4 lg:grid-cols-8">
                  {stats.members_by_vertical.map((v) => (
                    <div key={v.vertical || "unset"} className="rounded-md border border-border p-3">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {(v.vertical || "—").replace(/_/g, " ")}
                      </div>
                      <div className="mt-1 font-heading text-xl font-extrabold tabular-nums">{v.count}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Trial expiring soon — conversion ops priority */}
        <TrialExpiringAlert agencies={agencies} onDrill={openDrill} />

        {/* Agency list */}
        <Card className="mt-8">
          <CardHeader><CardTitle className="font-heading text-base">Client agencies ({agencies.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Agency</th>
                  <th className="px-4 py-3">Vertical</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3 text-right">Roster</th>
                  <th className="px-4 py-3 text-right">Admins</th>
                  <th className="px-4 py-3 text-right">30d ✓</th>
                  <th className="px-4 py-3 text-right">SOS</th>
                  <th className="px-4 py-3">Last activity</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {agencies.map((a) => (
                  <tr key={a.agency_id} data-testid={`platform-agency-row-${a.agency_id}`}
                      className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-medium">{a.name}</td>
                    <td className="px-4 py-3 capitalize text-muted-foreground">{(a.vertical || "—").replace(/_/g, " ")}</td>
                    <td className="px-4 py-3">
                      {a.plan === "trial" ? (
                        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                              style={{ background: "hsl(var(--haruki-leaf) / 0.15)", color: "hsl(var(--haruki-leaf-deep))" }}>
                          Trial · {a.trial_days_left ?? "?"}d
                        </span>
                      ) : (
                        <span className="text-muted-foreground capitalize">{a.plan || "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{a.roster_size}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{a.admin_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{a.completions_30d}</td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: a.sos_lifetime > 0 ? "hsl(var(--haruki-rust))" : "inherit" }}>
                      {a.sos_lifetime}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{a.last_activity_at?.slice(0, 10) || "—"}</td>
                    <td className="px-4 py-3">
                      <Button data-testid={`platform-drill-${a.agency_id}`}
                              variant="ghost" size="sm" onClick={() => openDrill(a.agency_id)}>
                        <Eye className="mr-1 h-3 w-3" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
                {agencies.length === 0 && !loading && (
                  <tr><td colSpan="9" className="px-4 py-8 text-center text-sm text-muted-foreground">No agencies yet.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Drill-down modal-ish panel */}
        {drill && (
          <Card data-testid="platform-drill-panel" className="mt-8 border-2"
                style={{ borderColor: "hsl(var(--haruki-teal))" }}>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle className="font-heading text-lg">{drill.agency.name}</CardTitle>
                <div className="mt-1 text-xs text-muted-foreground">
                  {drill.agency.vertical?.replace(/_/g, " ")} · {drill.agency.plan} · created {drill.agency.created_at?.slice(0, 10)}
                </div>
              </div>
              <Button variant="ghost" size="sm" data-testid="platform-drill-close"
                      onClick={() => setDrill(null)}>Close</Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-3">
                {Object.entries(drill.module_summary).map(([k, v]) => {
                  const titleMap = { new_hire: "New Hire", annual: "Annual", critical_incident: "Critical Incident" };
                  const rate = v.enrolled ? Math.round((v.completed / v.enrolled) * 100) : 0;
                  return (
                    <div key={k} className="rounded-md border border-border p-3">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{titleMap[k]}</div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <div className="font-heading text-2xl font-extrabold tabular-nums">{rate}%</div>
                        <div className="text-xs text-muted-foreground">{v.completed} / {v.enrolled}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Agency admins ({drill.admins.length})
                </div>
                <div className="space-y-1 text-sm">
                  {drill.admins.map((u) => (
                    <div key={u.user_id} className="flex items-center gap-3">
                      <span className="font-medium">{u.name}</span>
                      <span className="text-xs text-muted-foreground">{u.email}</span>
                    </div>
                  ))}
                  {drill.admins.length === 0 && <div className="text-xs text-muted-foreground">No admins yet.</div>}
                </div>
              </div>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Service members ({drill.members.length}) · SOS lifetime: {drill.sos_lifetime}
                </div>
                <div className="max-h-80 overflow-y-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border bg-secondary/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Vertical</th>
                        <th className="px-3 py-2">In-service</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drill.members.map((m) => (
                        <tr key={m.user_id} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 font-medium">{m.name}</td>
                          <td className="px-3 py-2 text-muted-foreground">{m.email}</td>
                          <td className="px-3 py-2 capitalize text-muted-foreground">{m.vertical?.replace(/_/g, " ")}</td>
                          <td className="px-3 py-2 text-muted-foreground">{m.in_service_date?.slice(0, 10) || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-10 flex items-start gap-3 rounded-md border border-border p-4 text-xs text-muted-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Confidentiality preserved here too:</strong> scenario content, individual HMDE scores, and SOS
            conversation content are not visible — even to platform admins. This view is participation rollup only.
          </span>
        </div>
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, subline, tone = "leaf", test }) => (
  <Card data-testid={test}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2">
        <div className="rounded-md p-1.5" style={{ background: TONE[tone], color: "white" }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 font-heading text-2xl font-extrabold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{subline}</div>
    </CardContent>
  </Card>
);


const TRIAL_URGENT_THRESHOLD = 5;

const TrialExpiringAlert = ({ agencies, onDrill }) => {
  const urgent = (agencies || [])
    .filter((a) => a.plan === "trial" && a.trial_days_left != null && a.trial_days_left < TRIAL_URGENT_THRESHOLD)
    .sort((a, b) => (a.trial_days_left ?? 0) - (b.trial_days_left ?? 0));

  if (urgent.length === 0) {
    return (
      <Card data-testid="trial-expiring-empty" className="mt-6">
        <CardContent className="flex items-center gap-3 p-5">
          <div className="rounded-md p-2" style={{ background: "hsl(var(--haruki-leaf) / 0.15)" }}>
            <CalendarClock className="h-4 w-4" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
          </div>
          <div>
            <div className="font-heading text-sm font-bold">No trials expiring this week</div>
            <div className="text-xs text-muted-foreground">
              You're alerted when any agency has fewer than {TRIAL_URGENT_THRESHOLD} days left on its trial.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const mostUrgent = urgent[0].trial_days_left;
  const tone = mostUrgent <= 1 ? "hsl(var(--haruki-fire))" : "hsl(var(--haruki-rust))";

  return (
    <Card data-testid="trial-expiring-alert"
          className="mt-6 border-2"
          style={{ borderColor: tone, background: `${tone.replace(")", " / 0.05)").replace("hsl(", "hsl(")}` }}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md p-2" style={{ background: tone, color: "white" }}>
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="font-heading text-base" style={{ color: tone }}>
              Trial expiring soon — {urgent.length} agenc{urgent.length === 1 ? "y" : "ies"}
            </CardTitle>
            <div className="mt-1 text-xs text-muted-foreground">
              Sorted most-urgent first. Reach out today for the &lt;{TRIAL_URGENT_THRESHOLD}-day window — this is your highest-leverage conversion moment.
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-y border-border bg-secondary/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-2">Agency</th>
              <th className="px-4 py-2">Vertical</th>
              <th className="px-4 py-2 text-right">Days left</th>
              <th className="px-4 py-2 text-right">Roster</th>
              <th className="px-4 py-2 text-right">30d ✓</th>
              <th className="px-4 py-2">Last activity</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {urgent.map((a) => {
              const rowTone = a.trial_days_left <= 1 ? "hsl(var(--haruki-fire))" : "hsl(var(--haruki-rust))";
              return (
                <tr key={a.agency_id}
                    data-testid={`trial-expiring-row-${a.agency_id}`}
                    className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-semibold">{a.name}</td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{(a.vertical || "—").replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="rounded-full px-2 py-0.5 text-xs font-bold tabular-nums"
                          style={{ background: rowTone, color: "white" }}>
                      {a.trial_days_left === 0 ? "Today" : `${a.trial_days_left}d`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{a.roster_size}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{a.completions_30d}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{a.last_activity_at?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`trial-expiring-drill-${a.agency_id}`}
                            variant="ghost" size="sm" onClick={() => onDrill(a.agency_id)}>
                      <Eye className="mr-1 h-3 w-3" /> View
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};
